/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { PropsWithChildren, useState, useMemo, ReactNode } from 'react';
import { use100vh } from 'react-div-100vh';
import { FiChevronLeft, FiMenu, FiX } from 'react-icons/fi';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { AnimatePresenceComponent } from '../AnimatePresenceComponent';
import FlyoutMenu from '../FlyoutMenu';

import {
  CopaDeAmigosIcon,
  EquipoDeFutbol5,
  EquipoDePrimeraIcon,
  FechaDeOroIcon,
  InicioIcon,
  MiPerfilIcon,
  MiniLigasIcon,
  MisMedallasIcon,
  MisPinesIcon,
  RankingIcon,
  SalirIcon,
  TDAIcon,
  TorneoDeEscuelasIcon,
  TorneosIcon,
  PreguntasFrecuentesIcon,
  PremiumCrown,
  Desafio1a1Icon,
} from './Icons';
import MenuLogo from './MenuLogo';
import Mobile from './Mobile';

import IconAlert from '@/assets/ic_alert_navbar.svg';
import useAuth from '@/hooks/useAuth';
import useConfirmLogout from '@/hooks/useConfirmLogout';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import NotificationsBell from '@/modules/notifications/components/NotificationsBell';
import ROUTES from '@/router/routes';
import { MakeOptional } from '@/types';
import { cn, nameAvatar } from '@/utils';

type NavbarTitleProps = {
  title: string | ReactNode;
  subTitle: string;
};

type DisplayLogoProps = {
  displayLogo: boolean;
};

type backAction = {
  backAction: () => void;
};

type ShowMenu = {
  showMenuAction: boolean;
};

type DefaultAction = {
  customRightAction: () => void;
  customRightContent: React.ReactNode;
};

type NotificationsMenu = {
  notificationsMenuAction: boolean;
};

type CloseMenu = {
  closeMenuActionUrl: string;
};

type Navigation = {
  items: boolean;
};

export type LeftContentType =
  | MakeOptional<backAction, 'backAction'>
  | MakeOptional<ShowMenu, 'showMenuAction'>
  | MakeOptional<DisplayLogoProps, 'displayLogo'>
  | MakeOptional<Navigation, 'items'>;

export type CenterContentType =
  | MakeOptional<NavbarTitleProps, 'subTitle'>
  | MakeOptional<DisplayLogoProps, 'displayLogo'>;

export type RightContentType =
  | MakeOptional<DefaultAction, 'customRightAction'>
  | MakeOptional<DefaultAction, 'customRightContent'>
  | MakeOptional<NotificationsMenu, 'notificationsMenuAction'>
  | MakeOptional<CloseMenu, 'closeMenuActionUrl'>;

export type MobileNav = {
  mobile?: {
    leftContent?: LeftContentType;
    centerContent?: CenterContentType;
    rightContent?: RightContentType;
  };
};

export interface PropsNavBar extends MobileNav {
  className?: string;
  desktop?: boolean;
  heightAuto?: 'auto' | undefined;
  userLogged?: any;
}

const menuActionClass =
  'rounded-[.63rem] bg-gdt-gray-9 w-8 h-8 flex justify-center items-center';

type NavigationItem = {
  name: string;
  href: string;
  icon?: ReactNode;
  highlight?: boolean;
  children?: NavigationItem[];
  disabled?: boolean;
  exceptionUrl?: string[]; // URLs where the item should be disabled
  isActive?: boolean;
};

const navigation: NavigationItem[] = [
  {
    name: 'Inicio',
    href: ROUTES.summary,
    icon: <InicioIcon variant="primary" />,
  },
  {
    name: 'Mi Equipo de Primera',
    href: ROUTES.myTeam11,
    icon: <EquipoDePrimeraIcon variant="primary" />,
  },
  {
    name: 'Mi Equipo de Fútbol 5',
    href: ROUTES.myTeam5,
    icon: <EquipoDeFutbol5 variant="primary" />,
  },
  {
    name: 'Torneos',
    href: ROUTES.tournaments,
    icon: <TorneosIcon variant="primary" />,
    highlight: false,
    children: [
      {
        name: 'Torneos de Amigos',
        href: ROUTES.friendsTournament,
        icon: <TDAIcon variant="primary" />,
        children: [
          {
            name: 'Torneos de Amigos Clásicos',
            href: ROUTES.friendsTournament,
          },
          {
            name: 'Torneos de Amigos por Premios',
            href: ROUTES.friendsTournamentPrizes,
          },
        ],
      },
      {
        name: 'Miniligas',
        href: ROUTES.minileagues.home,
        icon: <MiniLigasIcon variant="primary" />,
      },
      {
        name: 'Copa de Amigos',
        href: ROUTES.friendsCup.home,
        icon: <CopaDeAmigosIcon variant="primary" />,
      },
      {
        name: 'Fecha de Oro',
        href: ROUTES.goldDate.home,
        icon: <FechaDeOroIcon variant="primary" />,
        highlight: false,
      },
      {
        name: 'Torneo de Escuelas',
        href: ROUTES.primarySchoolTournament,
        icon: <TorneoDeEscuelasIcon variant="primary" />,
      },
      {
        name: 'Desafíos 1 a 1',
        href: ROUTES.challenge.home,
        icon: <Desafio1a1Icon variant="primary" />,
        exceptionUrl: [],
        highlight: false,
      },
    ],
  },
  {
    name: 'Rankings',
    href: ROUTES.ranking,
    icon: <RankingIcon variant="primary" />,
  },
  {
    name: 'Mis Pines',
    href: ROUTES.myAccount,
    icon: <MisPinesIcon variant="primary" />,
  },
  {
    name: 'Mis Medallas',
    href: ROUTES.medals,
    icon: <MisMedallasIcon variant="primary" />,
  },
];

const mobileNavigation: NavigationItem[] = [
  {
    name: 'Inicio',
    href: ROUTES.summary,
    icon: <InicioIcon variant="primary" />,
  },
  {
    name: 'Mi Equipo de Primera',
    href: ROUTES.myTeam11,
    icon: <EquipoDePrimeraIcon variant="primary" />,
  },
  {
    name: 'Mi Equipo de Fútbol 5',
    href: ROUTES.myTeam5,
    icon: <EquipoDeFutbol5 variant="primary" />,
  },
  {
    name: 'Rankings',
    href: ROUTES.ranking,
    icon: <RankingIcon variant="primary" />,
  },
  {
    name: 'Mis Pines',
    href: ROUTES.myAccount,
    icon: <MisPinesIcon variant="primary" />,
  },
  {
    name: 'Mis Medallas',
    href: ROUTES.medals,
    icon: <MisMedallasIcon variant="primary" />,
  },
  {
    name: 'Torneos',
    href: ROUTES.tournaments,
    icon: <TorneosIcon variant="primary" />,
    highlight: false,
    children: [
      {
        name: 'Torneos de Amigos',
        href: ROUTES.friendsTournament,
        icon: <TDAIcon variant="primary" />,
        children: [
          {
            name: 'Torneos de Amigos Clásicos',
            href: ROUTES.friendsTournament,
          },
          {
            name: 'Torneos de Amigos por Premios',
            href: ROUTES.friendsTournamentPrizes,
          },
        ],
      },
      {
        name: 'Miniligas',
        href: ROUTES.minileagues.home,
        icon: <MiniLigasIcon variant="primary" />,
      },
      {
        name: 'Copa de Amigos',
        href: ROUTES.friendsCup.home,
        icon: <CopaDeAmigosIcon variant="primary" />,
      },
      {
        name: 'Fecha de Oro',
        href: ROUTES.goldDate.home,
        icon: <FechaDeOroIcon variant="primary" />,
        highlight: false,
      },
      {
        name: 'Torneo de Escuelas',
        href: ROUTES.primarySchoolTournament,
        icon: <TorneoDeEscuelasIcon variant="primary" />,
      },
      {
        name: 'Desafíos 1 a 1',
        href: ROUTES.challenge.home,
        icon: <Desafio1a1Icon variant="primary" />,
        exceptionUrl: [],
        highlight: false,
      },
    ],
  },
];

export const NavBar = ({
  children,
  mobile,
  desktop,
  className,
  heightAuto = undefined,
}: PropsWithChildren<PropsNavBar>) => {
  const [toggleMobileMenu, setToggleMobileMenu] = useState(false);
  const { renderExitModal, showConfirmLogoutModal } = useConfirmLogout();
  const { isDesktop, isMobile } = useIsDesktop();
  const {
    user,
    isAuthenticated,
    userInitialData: userData,
    isLoading,
  } = useAuth();
  const { pinsData } = usePinsAccount();
  const height = use100vh();
  const location = useLocation();

  const handleToggleMobileMenu = (val?: boolean) => {
    setToggleMobileMenu(val ?? !toggleMobileMenu);
  };
  const processNavigation = (items: NavigationItem[]): NavigationItem[] =>
    items.map((item) => ({
      ...item,
      disabled: item.exceptionUrl
        ? item.exceptionUrl.includes(window.location.origin)
        : item.disabled,
      children: item.children ? processNavigation(item.children) : undefined,
      isActive: (() => {
        if (item.children) {
          return item.children.some(
            (child) =>
              location.pathname === child.href ||
              (child.children &&
                child.children.some(
                  (subChild) => location.pathname === subChild.href
                ))
          );
        }
        return (
          location.pathname === item.href ||
          location.pathname.split('/')[1] === item.href.split('/')[1]
        );
      })(),
    }));

  const navigationParsed = processNavigation(navigation);
  const navigationMobileParsed = processNavigation(mobileNavigation);

  const isActiveItemTournaments = useMemo(() => {
    const collectRoutes = (items: NavigationItem[]): string[] =>
      items.flatMap((item) => [
        item.href,
        ...(item.children ? collectRoutes(item.children) : []),
      ]);

    const tournamentsItem = navigation.find(
      (item) => item.href === ROUTES.tournaments
    );
    const allRoutes = [
      ROUTES.tournaments,
      ...collectRoutes(tournamentsItem?.children || []),
    ];

    return allRoutes.some((route) => location.pathname.includes(route));
  }, [location]);

  const settingsNavigation = useMemo(() => {
    const editProfileAction = {
      name: 'Editar Perfil',
      href: isDesktop ? ROUTES.editAccount.myData : ROUTES.editAccount.menu,
      icon: <MiPerfilIcon variant="primary" />,
    };

    const frequentlyAsksAction = {
      name: 'Preguntas Frecuentes',
      href: ROUTES.preguntasFrecuentes,
      icon: <PreguntasFrecuentesIcon variant="primary" />,
    };

    const logOutAction = {
      name: 'Salir',
      href: '#',
      icon: <SalirIcon variant={isDesktop ? 'tertiary' : 'secondary'} />,
      onClick: () => showConfirmLogoutModal(),
      isContrast: true,
    };

    return isDesktop
      ? [editProfileAction, logOutAction]
      : [editProfileAction, frequentlyAsksAction, logOutAction];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  if (mobile && isMobile) {
    const { centerContent, leftContent, rightContent } = mobile;

    const renderMobileLeftContent = () => {
      if (leftContent) {
        if (Object.keys(leftContent).length > 2) {
          throw new Error(
            "You can't use more than two property in leftContent"
          );
        }

        if ('backAction' in leftContent) {
          return (
            <button
              className={menuActionClass}
              type="button"
              onClick={leftContent.backAction}
            >
              <FiChevronLeft className="text-white w-6 h-6" />
            </button>
          );
        } else if ('showMenuAction' in leftContent) {
          const isItemsInLeftContent = 'items' in leftContent;

          if (!isItemsInLeftContent) {
            throw new Error(
              'If you are using showMenuAction you need to pass items in leftContent'
            );
          }

          return (
            <button
              className={menuActionClass}
              type="button"
              onClick={() => handleToggleMobileMenu()}
            >
              <FiMenu className="text-white w-4 h-4" />
            </button>
          );
        } else if ('displayLogo' in leftContent) {
          return <MenuLogo />;
        } else {
          return null;
        }
      }

      return null;
    };

    const renderMobileCenterContent = () => {
      if (centerContent) {
        if (Object.keys(centerContent).length > 1) {
          if ('displayLogo' in centerContent) {
            throw new Error(
              "You can't use displayLogo when you have more than one property in centerContent"
            );
          }
        }

        if ('title' in centerContent) {
          return (
            <div className="space-y-0">
              <p className="text-base text-white text-center font-semibold">
                {centerContent.title}
              </p>
              {centerContent.subTitle && (
                <p className="text-3xs text-white text-center opacity-70 uppercase">
                  {centerContent.subTitle}
                </p>
              )}
            </div>
          );
        } else if ('displayLogo' in centerContent) {
          return <MenuLogo />;
        } else {
          return null;
        }
      }

      return null;
    };

    const renderMobileRightContent = () => {
      if (rightContent) {
        if (Object.keys(rightContent).length > 1) {
          throw new Error(
            "You can't use more than one property in rightContent"
          );
        }

        if ('customRightContent' in rightContent) {
          return rightContent.customRightContent;
        } else if ('notificationsMenuAction' in rightContent) {
          return <NotificationsBell isPremium={pinsData.isPremium} />;
        } else if ('closeMenuActionUrl' in rightContent) {
          return (
            <Link
              className={menuActionClass}
              to={rightContent.closeMenuActionUrl!}
            >
              <FiX className="text-white w-5 h-5" />
            </Link>
          );
        } else if ('customRightAction' in rightContent) {
          return (
            <button
              className={menuActionClass}
              onClick={() => rightContent.customRightAction()}
            >
              <FiX className="text-white w-5 h-5" />
            </button>
          );
        } else {
          return null;
        }
      }

      return null;
    };

    return (
      <>
        {leftContent && 'items' in leftContent && (
          <Mobile
            bottomNavigation={settingsNavigation}
            handleOpen={(val) => handleToggleMobileMenu(val)}
            isActiveLink={isActiveItemTournaments}
            isOpen={toggleMobileMenu}
            navigation={navigationMobileParsed}
          />
        )}
        <AnimatePresenceComponent isLoaded={!isLoading}>
          <div
            className="flex flex-col"
            style={{ minHeight: heightAuto || `${height}px` }}
          >
            {renderExitModal()}
            <nav
              className={cn(
                'z-10 sticky top-0 min-h-[51px] ps-5 pe-6 flex lg:hidden bg-gdt-blue-header justify-between items-center',
                className
              )}
            >
              <figure className="w-9 flex justify-center">
                {renderMobileLeftContent()}
              </figure>
              <div className="flex-1">{renderMobileCenterContent()}</div>
              <div className="w-9 flex justify-center relative">
                {renderMobileRightContent()}
              </div>
            </nav>
            <div
              className="overflow-auto flex-1 flex flex-col bg-gdt-gray-6"
              style={{ minHeight: heightAuto || `calc(${height}px - 51px)` }}
            >
              {children}
            </div>
          </div>
        </AnimatePresenceComponent>
      </>
    );
  }

  if (desktop && isDesktop) {
    return (
      <div
        className="flex flex-col"
        style={{
          minHeight: heightAuto || `calc(${height}px + 70px)`,
        }}
      >
        <nav
          className={cn(
            'bg-gdt-blue-header min-h-[70px] w-full items-center justify-between px-8 lg:flex hidden navDesktop sticky top-0 z-40',
            {
              'bg-gdt-gray premium': pinsData.isPremium,
            }
          )}
        >
          <div className="flagGradient" />
          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start gap-9">
            <div>
              <MenuLogo className="max-w-[30px] min-w-[30px] w-full" />
            </div>
            <div className="flex items-center space-x-8">
              {navigationParsed.map((item, index) => {
                if (item.children) {
                  return (
                    <FlyoutMenu
                      key={`menu-flyout-${index}`}
                      className="flex items-center gap-2"
                      isActive={isActiveItemTournaments}
                      items={item.children}
                      showTDAP={userData?.showTDAP}
                    >
                      {item.highlight && <img src={IconAlert} />}
                      {item.name}
                    </FlyoutMenu>
                  );
                }

                const goToSection =
                  item.href === ROUTES.myTeam5 && !userData?.tiene_equipo_f5
                    ? ROUTES.welcomeF5
                    : item.href;

                return (
                  <NavLink
                    key={goToSection}
                    // eslint-disable-next-line no-constant-condition
                    aria-current={false ? 'page' : undefined}
                    className={cn(
                      'block text-sm font-semibold tracking-[0.5px] text-center text-white relative py-2 item--navbar',
                      {
                        active:
                          location.pathname === ROUTES.buildTeam5 &&
                          item.href === ROUTES.myTeam5,
                      }
                    )}
                    to={goToSection}
                  >
                    {item.name}
                    <span
                      className={cn(
                        'block h-[2px] w-full absolute bottom-[2px]'
                      )}
                    />
                  </NavLink>
                );
              })}
            </div>
          </div>
          <div className="flex items-center">
            {user && isAuthenticated && (
              <NotificationsBell isPremium={pinsData.isPremium} />
            )}
            {user && isAuthenticated && (
              <FlyoutMenu
                className="flex items-center gap-2"
                customClass={'-mt-1'}
                isPremium={pinsData.isPremium}
                items={settingsNavigation}
              >
                <figure
                  className={cn(
                    'relative h-[2.3rem] w-[2.3rem] rounded-full bg-gdt-blue-xl2 text-gdt-blue-header flex items-center justify-center border-white border-2 overflow-hidden',
                    {
                      'border-gdt-status-yellow text-gdt-status-yellow bg-gdt-status-yellow-light':
                        pinsData.isPremium,
                    }
                  )}
                >
                  {userData?.pathImagenPerfil && (
                    <img
                      className="w-full h-auto absolute"
                      src={userData?.pathImagenPerfil}
                    />
                  )}
                  <figcaption>
                    {userData && nameAvatar(userData.nombre_dt)}
                  </figcaption>
                </figure>
                <div>
                  <span className="block text-sm text-white">
                    {userData?.nombre_dt}
                  </span>
                  {pinsData.isPremium && (
                    <div className="flex items-center space-x-1 justify-start h-[22px] max-w-[6.25rem]">
                      <PremiumCrown />
                      <p className="text-gdt-status-yellow font-bold text-3xs ">
                        DT PREMIUM
                      </p>
                    </div>
                  )}
                </div>
              </FlyoutMenu>
            )}
          </div>
        </nav>
        {renderExitModal()}
        <AnimatePresenceComponent isLoaded={!isLoading}>
          <div
            style={{
              minHeight: heightAuto || `calc(${height}px - 70px)`,
            }}
          >
            {children}
          </div>
        </AnimatePresenceComponent>
      </div>
    );
  }

  return (
    <AnimatePresenceComponent isLoaded={!isLoading}>
      <div
        className="flex-1 flex"
        style={{
          minHeight: heightAuto || `calc(${height}px - 86px)`,
        }}
      >
        {children}
      </div>
    </AnimatePresenceComponent>
  );
};
